import React, { useState, useEffect } from 'react';
import { Plus, Gift, Minus, ChevronDown, ChevronUp, Users, UserPlus, X, Shield, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import './CreateTournament.css';

// ============================================
// TEMPORARY DISABLE FLAG - Set to false to re-enable tournament creation for all users
// When TOURNAMENT_CREATION_DISABLED is true, only admins/supervisors/approvers can create tournaments
// Regular players will see a "Coming Soon" notice
// ============================================
const TOURNAMENT_CREATION_DISABLED = false;
const TOURNAMENT_LAUNCH_DATE = '01.01.2026';
// ============================================

const CreateTournament = ({ user, onTournamentCreated, onUserUpdate, t }) => {
  const [isMinimized, setIsMinimized] = useState(true);
  const [formData, setFormData] = useState({
    tournamentType: 'regular',
    gameMode: 'battleRoyale',
    multiplayerType: 'solo',
    customMode: '',
    allowedWeapons: [],
    maxPlayers: 2,
    entryFee: 100,
    maxKills: 20,
    killLimit: 10,
    timeLimit: 300,
    map: 'Random',
    password: '',
    roomUrl: '',
    // NEW: BR Team Management
    brTeamPreference: 'random', // solo, random, squad
    brSquadmates: ['', '', ''], // For squad mode (up to 3 teammates for squad)
    // NEW: Multiplayer Team Management
    mpTeamPreference: 'random', // random, specific
    mpTeammates: ['', '', ''], // For team mode (up to 3 teammates for 4v4)
    // NEW: Giveaway creator participation mode: 'play', 'sponsor', 'spectate'
    creatorParticipation: 'play',
        waitForManualStart: false,
        openingDate: '',
        giveawayInfoUrl: '', // For giveaway - how creator participates: play (join match), sponsor (not in match), spectate (in game as spectator)
    // NEW: Giveaway additional options
    waitForManualStart: false, // Don't auto-start, wait for creator/admin to start
    openingDate: '', // Scheduled opening date
    giveawayInfoUrl: '' // URL for more info about the giveaway
  });
  const [loading, setLoading] = useState(false);
  const [commissionRates, setCommissionRates] = useState({
    multiplayer: { approver: 25, system: 25 },
    battleRoyale: { approver: 12.5, system: 12.5 }
  });

  const weaponTypes = [
    { id: 'boltActionSniper', name: t('boltActionSniper', 'Bolt-Action Sniper/Marksman'), icon: '🎯' },
    { id: 'shotgun', name: t('shotgun', 'Shotgun'), icon: '💥' },
    { id: 'assault', name: t('assault', 'Assault'), icon: '🔫' },
    { id: 'smg', name: t('smg', 'SMG'), icon: '⚡' }
  ];

  const codMobileMaps = {
    frontline: [
      'Random',
      'Armada Strike',
      'Seaside',
      'Rush',
      'Crossroads Strike',
      'Diesel',
      'Combine',
      'Collateral Strike',
      'Frequency',
      'Nuketown: Year Of The Dragon',
      'Cheshire Park',
      'Express',
      'Hijacked',
      'Summit',
      'Killhouse',
      'Crossfire',
      'Crash',
      'Scrapyard 2019',
      'Miami Strike',
      'Nuketown Russia',
      'Monastery',
      'Oasis'
    ],
    teamDeathmatch: [
      'Random',
      'Favela',
      'Apocalypse',
      'Khandor Hideout',
      'Diesel',
      'Crossroads Strike',
      'Express',
      'Icebreaker',
      'Vacant',
      'Hovec Sawmill',
      'Miami Strike',
      'Hardhat',
      'Hacienda',
      'Zoo',
      'Combine',
      'Frequency',
      'Collateral Strike',
      'Reclaim',
      'Cage',
      'Armada Strike',
      'Arsenal',
      'Rush',
      'Cheshire Park',
      'Nuketown: Year Of The Dragon',
      'Seaside',
      'Raid',
      'Firing Range',
      'Nuketown',
      'Summit',
      'Takeoff',
      'Meltdown',
      'Crash',
      'Standoff',
      'Crossfire',
      'Hijacked',
      'Killhouse',
      'Rust',
      'Dome',
      'Shipment',
      'Shoot House',
      'Scrapyard 2019',
      'Slums',
      'Suldal Harbor',
      'Terminal',
      'Highrise',
      'Monastery',
      'Nuketown Russia',
      'Satellite',
      'Hackney Yard'
    ],
    searchAndDestroy: [
      'Random',
      'Express',
      'Favela',
      'Crossroads Strike',
      'Kurohana Metropolis',
      'Apocalypse',
      'Hacienda',
      'Icebreaker',
      'Khandor Hideout',
      'Hardhat',
      'Diesel',
      'Combine',
      'Frequency',
      'Collateral Strike',
      'Zoo',
      'Cheshire Park',
      'Arsenal',
      'Rush',
      'Seaside',
      'Armada Strike',
      'Vacant',
      'Meltdown',
      'Raid',
      'Summit',
      'Takeoff',
      'Firing Range',
      'Standoff',
      'Crossfire',
      'Tunisia',
      'Crash',
      'Highrise',
      'Slums',
      'Suldal Harbor',
      'Hovec Sawmill',
      'Scrapyard 2019',
      'Dome',
      'Hackney Yard',
      'Terminal',
      'Satellite',
      'Coastal'
    ],
    searchAndRescue: [
      'Random',
      'Hackney Yard',
      'Terminal',
      'Summit',
      'Favela',
      'Hacienda',
      'Coastal',
      'Crash',
      'Standoff',
      'Crossfire',
      'Takeoff',
      'Raid',
      'Tunisia'
    ],
    domination: [
      'Random',
      'Favela',
      'Apocalypse',
      'Express',
      'Crossroads Strike',
      'Kurohana Metropolis',
      'Khandor Hideout',
      'Icebreaker',
      'Vacant',
      'Hacienda',
      'Miami Strike',
      'Hardhat',
      'Frequency',
      'Cheshire Park',
      'Combine',
      'Collateral Strike',
      'Zoo',
      'Nuketown: Year Of The Dragon',
      'Rush',
      'Diesel',
      'Arsenal',
      'Seaside',
      'Armada Strike',
      'Meltdown',
      'Raid',
      'Takeoff',
      'Highrise',
      'Summit',
      'Firing Range',
      'Standoff',
      'Crossfire',
      'Crash',
      'Nuketown',
      'Rust',
      'Suldal Harbor',
      'Dome',
      'Slums',
      'Hovec Sawmill',
      'Scrapyard 2019',
      'Shipment',
      'Hackney Yard',
      'Terminal',
      'Satellite',
      'Shoot House',
      'Nuketown Russia'
    ],
    hardpoint: [
      'Random',
      'Express',
      'Apocalypse',
      'Kurohana Metropolis',
      'Diesel',
      'Crossroads Strike',
      'Hacienda',
      'Icebreaker',
      'Hardhat',
      'Khandor Hideout',
      'Miami Strike',
      'Frequency',
      'Cheshire Park',
      'Combine',
      'Collateral Strike',
      'Zoo',
      'Arsenal',
      'Rush',
      'Armada Strike',
      'Nuketown: Year Of The Dragon',
      'Seaside',
      'Vacant',
      'Firing Range',
      'Nuketown',
      'Raid',
      'Summit',
      'Takeoff',
      'Standoff',
      'Crossfire',
      'Crash',
      'Hijacked',
      'Rust',
      'Suldal Harbor',
      'Shipment',
      'Slums',
      'Hovec Sawmill',
      'Scrapyard 2019',
      'Hackney Yard',
      'Terminal',
      'Satellite',
      'Shoot House',
      'Nuketown Russia'
    ],
    freeForAll: [
      'Random',
      'Favela',
      'Apocalypse',
      'Express',
      'Diesel',
      'Crossroads Strike',
      'Hacienda',
      'Icebreaker',
      'Hardhat',
      'Khandor Hideout',
      'Miami Strike',
      'Rush',
      'Combine',
      'Frequency',
      'Zoo',
      'Collateral Strike',
      'Cage',
      'Armada Strike',
      'Arsenal',
      'Seaside',
      'Cheshire Park',
      'Nuketown: Year Of The Dragon',
      'Nuketown',
      'Hijacked',
      'Firing Range',
      'Meltdown',
      'Raid',
      'Standoff',
      'Crossfire',
      'Crash',
      'Killhouse',
      'Rust',
      'Takeoff',
      'Slums',
      'Shipment',
      'Scrapyard 2019',
      'Vacant',
      'Hovec Sawmill',
      'Highrise',
      'Summit',
      'Terminal',
      'Nuketown Russia',
      'Satellite'
    ],
    killConfirmed: [
      'Random',
      'Express',
      'Favela',
      'Crossroads Strike',
      'Rush',
      'Diesel',
      'Hardhat',
      'Hacienda',
      'Miami Strike',
      'Apocalypse',
      'Khandor Hideout',
      'Zoo',
      'Combine',
      'Cage',
      'Collateral Strike',
      'Reclaim',
      'Armada Strike',
      'Arsenal',
      'Seaside',
      'Frequency',
      'Cheshire Park',
      'Icebreaker',
      'Firing Range',
      'Nuketown',
      'Raid',
      'Summit',
      'Meltdown',
      'Crash',
      'Standoff',
      'Rust',
      'Hijacked',
      'Killhouse',
      'Dome',
      'Shipment',
      'Slums',
      'Vacant',
      'Hovec Sawmill',
      'Hackney Yard',
      'Highrise',
      'Satellite',
      'Shoot House',
      'Nuketown Russia'
    ],
    battleRoyale: [
      'Random',
      'Isolated',
      'Blackout',
      'Alcatraz'
    ],
    quickSolo: [
      'Shipment'
    ]
  };

  useEffect(() => {
    loadCommissionRates();
  }, []);

  const loadCommissionRates = async () => {
    try {
      const response = await axios.get('/api/tournaments/commission-rates');
      setCommissionRates(response.data);
    } catch (error) {
      console.error('Failed to load commission rates:', error);
    }
  };

  const getActualCommissionRate = () => {
    const gameType = formData.gameMode === 'battleRoyale' ? 'battleRoyale' : 'multiplayer';
    const rates = commissionRates[gameType];
    
    if (!rates) {
      return gameType === 'battleRoyale' ? 25 : 50;
    }
    
    return (rates.approver || 0) + (rates.system || 0);
  };

  const calculatePrizePool = () => {
    if (formData.tournamentType === 'giveaway') {
      return formData.entryFee;
    } else {
      const totalEntry = formData.entryFee * formData.maxPlayers;
      const totalCommissionRate = getActualCommissionRate();
      const commissionDecimal = totalCommissionRate / 100;
      return Math.floor(totalEntry * (1 - commissionDecimal));
    }
  };

  // Check if user is admin/supervisor/approver (no commission for giveaways)
  const isAdminRole = ['main_admin', 'manager', 'supervisor', 'approver', 'verified_sponsor'].includes(user?.role);

  const calculateCreatorCost = () => {
    if (formData.tournamentType === 'giveaway') {
      // No commission for admins/supervisors/approvers on giveaway matches
      if (isAdminRole) {
        return formData.entryFee;
      }
      const totalCommissionRate = getActualCommissionRate();
      const commission = Math.ceil(formData.entryFee * totalCommissionRate / 100);
      return formData.entryFee + commission;
    } else {
      return formData.entryFee;
    }
  };

  const getCommissionBreakdown = () => {
    // No commission for admins/supervisors/approvers on giveaway matches
    if (formData.tournamentType === 'giveaway' && isAdminRole) {
      return {
        totalRate: 0,
        approverRate: 0,
        systemRate: 0,
        approverCommissionAmount: 0,
        systemCommissionAmount: 0,
        totalCommissionAmount: 0,
        loading: false,
        noCommission: true
      };
    }

    const totalCommissionRate = getActualCommissionRate();
    const gameType = formData.gameMode === 'battleRoyale' ? 'battleRoyale' : 'multiplayer';
    const rates = commissionRates[gameType];

    if (!rates) {
      const fallbackApprover = gameType === 'battleRoyale' ? 12.5 : 25;
      const fallbackSystem = gameType === 'battleRoyale' ? 12.5 : 25;
      const fallbackTotal = fallbackApprover + fallbackSystem;

      if (formData.tournamentType === 'giveaway') {
        const totalCommissionAmount = Math.ceil(formData.entryFee * fallbackTotal / 100);
        const approverCommissionAmount = Math.ceil(formData.entryFee * fallbackApprover / 100);
        const systemCommissionAmount = totalCommissionAmount - approverCommissionAmount;

        return {
          totalRate: fallbackTotal,
          approverRate: fallbackApprover,
          systemRate: fallbackSystem,
          approverCommissionAmount,
          systemCommissionAmount,
          totalCommissionAmount,
          loading: true
        };
      } else {
        const totalEntry = formData.entryFee * formData.maxPlayers;

        return {
          totalRate: fallbackTotal,
          approverRate: fallbackApprover,
          systemRate: fallbackSystem,
          approverCommissionAmount: Math.floor(totalEntry * fallbackApprover / 100),
          systemCommissionAmount: Math.floor(totalEntry * fallbackSystem / 100),
          totalCommissionAmount: Math.floor(totalEntry * fallbackTotal / 100),
          loading: true
        };
      }
    }

    const approverRate = rates.approver || (gameType === 'battleRoyale' ? 12.5 : 25);
    const systemRate = rates.system || (gameType === 'battleRoyale' ? 12.5 : 25);

    if (formData.tournamentType === 'giveaway') {
      const totalCommissionAmount = Math.ceil(formData.entryFee * totalCommissionRate / 100);
      const approverCommissionAmount = Math.ceil(formData.entryFee * approverRate / 100);
      const systemCommissionAmount = totalCommissionAmount - approverCommissionAmount;

      return {
        totalRate: totalCommissionRate,
        approverRate: approverRate,
        systemRate: systemRate,
        approverCommissionAmount,
        systemCommissionAmount,
        totalCommissionAmount,
        loading: false
      };
    } else {
      const totalEntry = formData.entryFee * formData.maxPlayers;

      return {
        totalRate: totalCommissionRate,
        approverRate: approverRate,
        systemRate: systemRate,
        approverCommissionAmount: Math.floor(totalEntry * approverRate / 100),
        systemCommissionAmount: Math.floor(totalEntry * systemRate / 100),
        totalCommissionAmount: Math.floor(totalEntry * totalCommissionRate / 100),
        loading: false
      };
    }
  };

  // NEW: Handle squadmate input changes
  const handleSquadmateChange = (index, value) => {
    const newSquadmates = [...formData.brSquadmates];
    newSquadmates[index] = value;
    setFormData({...formData, brSquadmates: newSquadmates});
  };

  // NEW: Toggle squadmate slot between specific/random/empty
  const toggleSquadmateSlot = (index) => {
    const newSquadmates = [...formData.brSquadmates];
    const currentValue = newSquadmates[index];

    if (currentValue === '') {
      // Empty -> Random
      newSquadmates[index] = '__RANDOM__';
    } else if (currentValue === '__RANDOM__') {
      // Random -> Empty
      newSquadmates[index] = '';
    } else {
      // Specific player -> Random
      newSquadmates[index] = '__RANDOM__';
    }

    setFormData({...formData, brSquadmates: newSquadmates});
  };

  // NEW: Handle MP teammate input changes
  const handleMpTeammateChange = (index, value) => {
    const newTeammates = [...formData.mpTeammates];
    newTeammates[index] = value;
    setFormData({...formData, mpTeammates: newTeammates});
  };

  // NEW: Toggle MP teammate slot between specific/random/empty
  const toggleMpTeammateSlot = (index) => {
    const newTeammates = [...formData.mpTeammates];
    const currentValue = newTeammates[index];

    if (currentValue === '') {
      // Empty -> Random
      newTeammates[index] = '__RANDOM__';
    } else if (currentValue === '__RANDOM__') {
      // Random -> Empty
      newTeammates[index] = '';
    } else {
      // Specific player -> Random
      newTeammates[index] = '__RANDOM__';
    }

    setFormData({...formData, mpTeammates: newTeammates});
  };

  // Get max MP teammates based on max players (for team games, half of maxPlayers minus 1 for creator)
  const getMaxMpTeammates = () => {
    const teamSize = formData.maxPlayers / 2;
    return teamSize - 1; // Subtract 1 for the creator
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const creatorCost = calculateCreatorCost();
    if (user.points < creatorCost) {
      toast.error(t('insufficientPointsDetailed', 'Insufficient points! You need {needed} points but only have {current} points.')
        .replace('{needed}', creatorCost)
        .replace('{current}', user.points));
      return;
    }

    setLoading(true);
    try {
      const submissionData = { ...formData };
      
      if (['battleRoyale', 'searchAndDestroy', 'searchAndRescue'].includes(formData.gameMode)) {
        delete submissionData.maxKills;
      }
      
      if (formData.gameMode === 'quickSolo') {
        delete submissionData.maxKills;
        submissionData.killLimit = formData.killLimit;
        submissionData.timeLimit = formData.timeLimit;
        submissionData.allowedWeapons = formData.allowedWeapons || [];
      } else {
        delete submissionData.killLimit;
        delete submissionData.timeLimit;
        delete submissionData.allowedWeapons;
      }
      
      // Include BR team settings for Duo/Squad tournaments
      if (formData.gameMode === 'battleRoyale' && ['duo', 'squad'].includes(formData.multiplayerType)) {
        submissionData.brTeamPreference = formData.brTeamPreference;
        // For 'squad' mode, include squadmates; for others, send empty array
        if (formData.brTeamPreference === 'squad') {
          submissionData.brSquadmates = formData.brSquadmates.filter(name => name !== '');
        } else {
          submissionData.brSquadmates = [];
        }
      } else {
        delete submissionData.brTeamPreference;
        delete submissionData.brSquadmates;
      }

      // Include MP team settings for team games (not BR, not quickSolo)
      const mpTeamGameModes = ['teamDeathmatch', 'domination', 'searchAndDestroy', 'searchAndRescue', 'hardpoint', 'killConfirmed', 'frontline'];
      if (mpTeamGameModes.includes(formData.gameMode) && formData.multiplayerType === 'team') {
        submissionData.mpTeamPreference = formData.mpTeamPreference;
        // For 'specific' mode, include teammates as objects with {name, isRandom}
        if (formData.mpTeamPreference === 'specific') {
          submissionData.mpTeammates = formData.mpTeammates
            .filter(name => name !== '') // Filter out empty slots
            .map(name => ({
              name: name === '__RANDOM__' ? '' : name,
              isRandom: name === '__RANDOM__'
            }));
        } else {
          submissionData.mpTeammates = [];
        }
      } else {
        delete submissionData.mpTeamPreference;
        delete submissionData.mpTeammates;
      }

      delete submissionData.customMode;

      // Include creatorParticipation option for giveaway tournaments
      if (formData.tournamentType === 'giveaway') {
        submissionData.creatorParticipation = formData.creatorParticipation;
        // For backward compatibility, also set creatorJoins
        submissionData.creatorJoins = formData.creatorParticipation === 'play';
        // NEW: Additional giveaway options
        submissionData.waitForManualStart = formData.waitForManualStart;
        submissionData.openingDate = formData.openingDate || null;
        submissionData.giveawayInfoUrl = formData.giveawayInfoUrl || '';
      }

      await axios.post('/api/tournaments', submissionData);
      toast.success(t('tournamentCreated', 'Tournament created successfully!'));
      onTournamentCreated();
      onUserUpdate();

      setFormData({
        tournamentType: 'regular',
        gameMode: 'teamDeathmatch',
        multiplayerType: 'team',
        customMode: '',
        allowedWeapons: [],
        maxPlayers: 2,
        entryFee: 100,
        maxKills: 20,
        killLimit: 10,
        timeLimit: 300,
        map: 'Random',
        password: '',
        roomUrl: '',
        brTeamPreference: 'random',
        brSquadmates: ['', '', ''],
        mpTeamPreference: 'random',
        mpTeammates: ['', '', ''],
        creatorParticipation: 'play',
        waitForManualStart: false,
        openingDate: '',
        giveawayInfoUrl: ''
      });
      setIsMinimized(true);
    } catch (error) {
      const message = error.response?.data?.message || t('failedToCreateTournament', 'Failed to create tournament');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Check if user can create tournaments (admins/supervisors/approvers can always create)
  const canCreateTournament = !TOURNAMENT_CREATION_DISABLED ||
    ['main_admin', 'manager', 'supervisor', 'approver', 'verified_sponsor'].includes(user?.role);

  const toggleMinimized = () => {
    // If tournament creation is disabled for regular players, show coming soon notice
    if (isMinimized && !canCreateTournament) {
      toast(
        t('comingSoon', 'Coming Soon') + ' ' + TOURNAMENT_LAUNCH_DATE,
        {
          icon: '🚀',
          duration: 4000,
          style: {
            background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)',
            color: '#fbbf24',
            border: '1px solid #f59e0b',
            fontWeight: 'bold',
            fontSize: '1.1rem'
          }
        }
      );
      return;
    }
    setIsMinimized(!isMinimized);
  };

  // NEW: Get max squadmates based on BR type
  const getMaxSquadmates = () => {
    if (formData.gameMode !== 'battleRoyale') return 0;
    if (formData.multiplayerType === 'duo') return 1;
    if (formData.multiplayerType === 'squad') return 3;
    return 0;
  };

  // Only show BR team management if creator is actually playing (not spectating or sponsoring in giveaways)
  const isCreatorPlaying = formData.tournamentType !== 'giveaway' || formData.creatorParticipation === 'play';
  const showBRTeamManagement = formData.gameMode === 'battleRoyale' && ['duo', 'squad'].includes(formData.multiplayerType) && isCreatorPlaying;

  return (
    <div className="create-tournament-container create-tournament-enhanced">
      <div 
        className={`create-tournament-header ${isMinimized ? 'minimized' : 'expanded'}`}
        onClick={isMinimized ? toggleMinimized : undefined}
      >
        <div className="create-tournament-header-content">
          <Plus className="w-5 h-5 text-orange-400 pulse-icon" />
          <h3 className="create-tournament-title">{t('createTournament', 'Create Tournament')}</h3>
        </div>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleMinimized();
          }}
          className="create-tournament-toggle"
        >
          {isMinimized ? (
            <ChevronDown className="w-5 h-5" />
          ) : (
            <ChevronUp className="w-5 h-5" />
          )}
        </button>
      </div>

      {!isMinimized && (
        <div className="create-tournament-form-container">
          <form onSubmit={handleSubmit} className="create-tournament-form">
            <div className="create-tournament-grid">
              {/* Tournament Type */}
              <div className="create-tournament-field col-span-2">
                <label className="create-tournament-label">{t('tournamentType', 'Tournament Type')}</label>
                <select
                  value={formData.tournamentType}
                  onChange={(e) => setFormData({
                    ...formData,
                    tournamentType: e.target.value,
                    creatorParticipation: e.target.value === 'regular' ? 'play' : formData.creatorParticipation
                  })}
                  className="create-tournament-select"
                  disabled={loading}
                >
                  <option value="regular">{t('regularTournament', '🏆 Regular Tournament')}</option>
                  {['main_admin', 'manager', 'supervisor', 'approver', 'verified_sponsor'].includes(user?.role) && (
                    <option value="giveaway">{t('giveawayTournament', '🎁 Giveaway Tournament (Free Entry)')}</option>
                  )}
                </select>
              </div>

              {/* Giveaway Creator Participation Option */}
              {formData.tournamentType === 'giveaway' && (
                <div className="create-tournament-field col-span-2">
                  <label className="create-tournament-label">
                    <Gift className="w-4 h-4 inline mr-2" />
                    {t('creatorParticipation', 'Your Participation')}
                  </label>
                  <div className="giveaway-participation-options three-options">
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, creatorParticipation: 'play',
        waitForManualStart: false,
        openingDate: '',
        giveawayInfoUrl: ''})}
                      className={`participation-option ${formData.creatorParticipation === 'play' ? 'active' : ''}`}
                      disabled={loading}
                    >
                      <span className="participation-icon">🎮</span>
                      <span className="participation-title">{t('joinAndPlay', 'Join & Play')}</span>
                      <span className="participation-desc">{t('joinAndPlayDesc', 'You participate in the match')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, creatorParticipation: 'spectate'})}
                      className={`participation-option ${formData.creatorParticipation === 'spectate' ? 'active' : ''}`}
                      disabled={loading}
                    >
                      <span className="participation-icon">👀</span>
                      <span className="participation-title">{t('spectateGame', 'Spectate Game')}</span>
                      <span className="participation-desc">{t('spectateGameDesc', 'Watch the match as spectator')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, creatorParticipation: 'sponsor'})}
                      className={`participation-option ${formData.creatorParticipation === 'sponsor' ? 'active' : ''}`}
                      disabled={loading}
                    >
                      <span className="participation-icon">💰</span>
                      <span className="participation-title">{t('sponsorOnly', 'Sponsor Only')}</span>
                      <span className="participation-desc">{t('sponsorOnlyDesc', 'Just fund the prize, others play')}</span>
                    </button>
                  </div>
                  {formData.creatorParticipation === 'spectate' && (
                    <div className="sponsor-info-box spectate-info">
                      👀 {t('spectateInfo', 'You will be in the game as a SPECTATOR. You can watch but not play. All player slots are open for others.')}
                    </div>
                  )}
                  {formData.creatorParticipation === 'sponsor' && (
                    <div className="sponsor-info-box">
                      💰 {t('sponsorInfo', 'You will NOT be added to the match. All slots are open for other players to join freely.')}
                    </div>
                  )}
                  {/* NEW: Wait for manual start checkbox */}
                  <div className="giveaway-option-row">
                    <label className="giveaway-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.waitForManualStart}
                        onChange={(e) => setFormData({...formData, waitForManualStart: e.target.checked})}
                        disabled={loading}
                      />
                      <span className="checkbox-text">
                        ⏸️ {t("waitForManualStart", "Wait for my start")}
                      </span>
                    </label>
                    <span className="giveaway-option-hint">
                      {t("waitForManualStartHint", "Tournament will not auto-start when full. You or an admin must start it manually.")}
                    </span>
                  </div>

                  {/* NEW: Opening Date */}
                  <div className="giveaway-option-row">
                    <label className="giveaway-field-label">
                      📅 {t("openingDate", "Opening Date")} <span className="optional-label">({t("optional", "Optional")})</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.openingDate}
                      onChange={(e) => setFormData({...formData, openingDate: e.target.value})}
                      className="giveaway-date-input"
                      disabled={loading}
                    />
                    <span className="giveaway-option-hint">
                      {t("openingDateHint", "Display when this giveaway will start")}
                    </span>
                  </div>

                  {/* NEW: Info URL */}
                  <div className="giveaway-option-row">
                    <label className="giveaway-field-label">
                      🔗 {t("infoUrl", "Info URL")} <span className="optional-label">({t("optional", "Optional")})</span>
                    </label>
                    <input
                      type="url"
                      value={formData.giveawayInfoUrl}
                      onChange={(e) => setFormData({...formData, giveawayInfoUrl: e.target.value})}
                      className="giveaway-url-input"
                      placeholder="https://..."
                      disabled={loading}
                    />
                    <span className="giveaway-option-hint">
                      {t("infoUrlHint", "Link to more information about this giveaway")}
                    </span>
                  </div>
                </div>
              )}

              {/* Game Mode */}
              <div className="create-tournament-field">
                <label className="create-tournament-label">{t('gameMode', 'Game Mode')}</label>
                <select
                  value={formData.gameMode}
                  onChange={(e) => setFormData({
                    ...formData, 
                    gameMode: e.target.value,
                    multiplayerType: e.target.value === 'battleRoyale' ? 'solo' : e.target.value === 'quickSolo' ? 'team' : 'team',
                    customMode: '',
                    allowedWeapons: [],
                    maxPlayers: e.target.value === 'quickSolo' ? 2 : 2,
                    maxKills: ['freeForAll', 'quickSolo'].includes(e.target.value) ? undefined : 20,
                    killLimit: e.target.value === 'quickSolo' ? 10 : undefined,
                    timeLimit: e.target.value === 'quickSolo' ? 300 : undefined,
                    map: e.target.value === 'quickSolo' ? 'Shipment' : e.target.value === 'battleRoyale' ? 'Isolated' : 'Random',
                    brTeamPreference: 'random',
                    brSquadmates: ['', '', '']
                  })}
                  className="create-tournament-select"
                  disabled={loading}
                >
                  <option value="battleRoyale">👑 Battle Royale</option>
                  <option value="teamDeathmatch">⚔️ Team Deathmatch</option>
                  <option value="domination">🏁 Domination</option>
                  <option value="searchAndDestroy">💣 Search & Destroy</option>
                  <option value="searchAndRescue">🚁 Search & Rescue</option>
                  <option value="hardpoint">📍 Hardpoint</option>
                  {/* <option value="killConfirmed">🏷️ Kill Confirmed</option> */}
                  <option value="frontline">⚡ Frontline</option>
                  <option value="freeForAll">🎯 Free for All</option>
                  <option value="quickSolo">⚡ Quick Solo (1v1)</option>
                </select>
              </div>

              {/* Multiplayer Type */}
              {['teamDeathmatch', 'domination', 'searchAndDestroy', 'searchAndRescue', 'hardpoint', 'killConfirmed', 'frontline'].includes(formData.gameMode) && (
                <div className="create-tournament-field">
                  <label className="create-tournament-label">{t('multiplayerType', 'Match Type')}</label>
                  <select
                    value={formData.multiplayerType}
                    onChange={(e) => setFormData({
                      ...formData,
                      multiplayerType: e.target.value,
                      maxPlayers: 2
                    })}
                    className="create-tournament-select"
                    disabled={loading}
                  >
                    <option value="team">{t('teamMatch', 'Team Match')}</option>
                  </select>
                </div>
              )}

              {/* MP Team Selection - For team multiplayer games */}
              {['teamDeathmatch', 'domination', 'searchAndDestroy', 'searchAndRescue', 'hardpoint', 'killConfirmed', 'frontline'].includes(formData.gameMode) &&
               formData.multiplayerType === 'team' && (
                <div className="create-tournament-field col-span-2">
                  <label className="create-tournament-label">
                    <Users className="inline-icon" /> {t('teamSelection', 'Team Selection')}
                  </label>

                  <div className="team-preference-selector">
                    <select
                      value={formData.mpTeamPreference}
                      onChange={(e) => setFormData({
                        ...formData,
                        mpTeamPreference: e.target.value,
                        mpTeammates: ['', '', '']
                      })}
                      className="create-tournament-select"
                      disabled={loading}
                    >
                      <option value="random">{t('randomTeammate', '🎲 Random - Any player can join your team')}</option>
                      <option value="specific">{t('specificTeammate', '👥 Specific - Choose your teammate(s)')}</option>
                    </select>

                    {formData.mpTeamPreference === 'specific' && getMaxMpTeammates() > 0 && (
                      <div className="squad-selection">
                        <div className="squad-header">
                          <Users className="w-4 h-4" />
                          <span>{t('selectTeammates', 'Select Teammate(s)')} ({getMaxMpTeammates()})</span>
                        </div>

                        {Array.from({ length: getMaxMpTeammates() }).map((_, index) => {
                          const value = formData.mpTeammates[index] || '';
                          const isRandom = value === '__RANDOM__';
                          const isEmpty = value === '';

                          return (
                            <div key={index} className="squadmate-slot">
                              <div className="squadmate-input-group">
                                <input
                                  type="text"
                                  value={isRandom ? '' : value}
                                  onChange={(e) => handleMpTeammateChange(index, e.target.value)}
                                  placeholder={
                                    isRandom ? t('randomPlayer', '🎲 Random Player') :
                                    isEmpty ? t('enterPlayerName', 'Enter player in-game name') :
                                    t('playerName', 'Player Name')
                                  }
                                  className={`create-tournament-input ${isRandom ? 'random-slot' : isEmpty ? 'empty-slot' : ''}`}
                                  disabled={loading || isRandom}
                                />
                                <button
                                  type="button"
                                  onClick={() => toggleMpTeammateSlot(index)}
                                  className="squadmate-toggle-btn"
                                  title={
                                    isEmpty ? t('setRandom', 'Set to Random') :
                                    isRandom ? t('clearSlot', 'Clear Slot') :
                                    t('setRandom', 'Set to Random')
                                  }
                                >
                                  {isEmpty ? '🎲' : isRandom ? '❌' : '🎲'}
                                </button>
                              </div>
                              <div className="squadmate-status">
                                {isEmpty && <span className="status-empty">⚪ {t('enterName', 'Enter name or set random')}</span>}
                                {isRandom && <span className="status-random">🎲 {t('randomPlayer', 'Random Player')}</span>}
                                {!isEmpty && !isRandom && <span className="status-specific">✅ {t('specificPlayer', 'Specific Player')}</span>}
                              </div>
                            </div>
                          );
                        })}

                        <div className="squad-help-text">
                          💡 {t('mpTeamHelpText', 'Enter the exact in-game name of your teammate, or set to random')}
                        </div>
                      </div>
                    )}

                    {formData.mpTeamPreference === 'random' && (
                      <div className="br-info-box random-info">
                        🎲 {t('randomTeamInfo', 'Any available player can join your team when they join the tournament.')}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Battle Royale Type */}
              {formData.gameMode === 'battleRoyale' && (
                <div className="create-tournament-field">
                  <label className="create-tournament-label">{t('battleRoyaleType', 'Battle Royale Type')}</label>
                  <select
                    value={formData.multiplayerType || 'solo'}
                    onChange={(e) => setFormData({
                      ...formData, 
                      multiplayerType: e.target.value,
                      maxPlayers: e.target.value === 'solo' ? 2 : e.target.value === 'duo' ? 4 : 8,
                      brTeamPreference: 'random',
                      brSquadmates: ['', '', '']
                    })}
                    className="create-tournament-select"
                    disabled={loading}
                  >
                    <option value="solo">{t('solo', 'Solo')}</option>
                    <option value="duo">{t('duo', 'Duo')}</option>
                    <option value="squad">{t('squad', 'Squad')}</option>
                  </select>
                </div>
              )}

              {/* NEW: BR Team Management */}
              {showBRTeamManagement && (
                <div className="create-tournament-field col-span-2">
                  <label className="create-tournament-label">
                    <Shield className="w-4 h-4 inline mr-2" />
                    {t('teamPreference', 'Team Preference')}
                  </label>
                  
                  <div className="br-team-management">
                    <select
                      value={formData.brTeamPreference}
                      onChange={(e) => setFormData({
                        ...formData,
                        brTeamPreference: e.target.value,
                        brSquadmates: ['', '', '']
                      })}
                      className="create-tournament-select mb-3"
                      disabled={loading}
                    >
                      <option value="solo">{t('soloTeam', '👤 Solo - No teammates allowed')}</option>
                      <option value="random">{t('randomTeam', '🎲 Random - Any player can join')}</option>
                      <option value="squad">{t('squadTeam', '👥 Squad - Choose specific teammates')}</option>
                    </select>

                    {formData.brTeamPreference === 'squad' && (
                      <div className="squad-selection">
                        <div className="squad-header">
                          <Users className="w-4 h-4" />
                          <span>{t('selectSquadmates', 'Select Squadmates')} ({formData.multiplayerType === 'duo' ? '1' : 'Up to 3'})</span>
                        </div>
                        
                        {Array.from({ length: getMaxSquadmates() }).map((_, index) => {
                          const value = formData.brSquadmates[index] || '';
                          const isRandom = value === '__RANDOM__';
                          const isEmpty = value === '';
                          
                          return (
                            <div key={index} className="squadmate-slot">
                              <div className="squadmate-input-group">
                                <input
                                  type="text"
                                  value={isRandom ? '' : value}
                                  onChange={(e) => handleSquadmateChange(index, e.target.value)}
                                  placeholder={
                                    isRandom ? t('randomPlayer', '🎲 Random Player') :
                                    isEmpty ? t('emptySlot', 'Empty Slot (Optional)') :
                                    t('playerName', 'Player Name')
                                  }
                                  className={`create-tournament-input ${isRandom ? 'random-slot' : isEmpty ? 'empty-slot' : ''}`}
                                  disabled={loading || isRandom}
                                />
                                <button
                                  type="button"
                                  onClick={() => toggleSquadmateSlot(index)}
                                  className="squadmate-toggle-btn"
                                  title={
                                    isEmpty ? t('setRandom', 'Set to Random') :
                                    isRandom ? t('clearSlot', 'Clear Slot') :
                                    t('setRandom', 'Set to Random')
                                  }
                                >
                                  {isEmpty ? '🎲' : isRandom ? '❌' : '🎲'}
                                </button>
                              </div>
                              <div className="squadmate-status">
                                {isEmpty && <span className="status-empty">⚪ {t('optional', 'Optional')}</span>}
                                {isRandom && <span className="status-random">🎲 {t('randomPlayer', 'Random Player')}</span>}
                                {!isEmpty && !isRandom && <span className="status-specific">✅ {t('specific', 'Specific Player')}</span>}
                              </div>
                            </div>
                          );
                        })}
                        
                        <div className="squad-help-text">
                          💡 {t('squadHelpText', 'Toggle between specific player name, random player, or empty slot')}
                        </div>
                      </div>
                    )}

                    {formData.brTeamPreference === 'solo' && (
                      <div className="br-info-box solo-info">
                        👤 {t('soloInfo', 'You will play alone. No one can join your team.')}
                      </div>
                    )}

                    {formData.brTeamPreference === 'random' && (
                      <div className="br-info-box random-info">
                        🎲 {t('randomInfo', 'Any available player can join your team.')}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Weapon Restrictions - Only for Quick Solo */}
              {formData.gameMode === 'quickSolo' && (
                <div className="create-tournament-field col-span-2">
                  <label className="create-tournament-label">{t('weaponRestrictions', 'Weapon Restriction (Optional)')}</label>
                  
                  <div className="weapon-selection">
                    <div className="weapon-selection-help">
                      {t('selectOneWeapon', 'Select one weapon type to restrict the match (leave empty for all weapons)')}
                    </div>
                    
                    <div className="weapon-grid">
                      {weaponTypes.map(weapon => {
                        const isSelected = formData.allowedWeapons.includes(weapon.id);
                        return (
                          <button
                            key={weapon.id}
                            type="button"
                            onClick={() => {
                              const newAllowedWeapons = isSelected 
                                ? []
                                : [weapon.id];
                              setFormData({...formData, allowedWeapons: newAllowedWeapons});
                            }}
                            disabled={loading}
                            className={`weapon-btn ${isSelected ? 'selected' : ''}`}
                          >
                            <span className="weapon-icon">{weapon.icon}</span>
                            <span className="weapon-name">{weapon.name}</span>
                            {isSelected && <span className="weapon-check">✓</span>}
                          </button>
                        );
                      })}
                    </div>
                    
                    {formData.allowedWeapons.length > 0 && (
                      <div className="selected-weapons">
                        <div className="selected-weapons-header">
                          {t('selectedWeapon', 'Selected Weapon Type')}:
                        </div>
                        {formData.allowedWeapons.map(weaponId => {
                          const weapon = weaponTypes.find(w => w.id === weaponId);
                          return weapon ? (
                            <span key={weaponId} className="selected-weapon-tag">
                              {weapon.icon} {weapon.name}
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                    
                    {formData.allowedWeapons.length === 0 && (
                      <div className="no-weapons-selected">
                        {t('allWeaponsAllowed', 'All weapons allowed - no restrictions applied')}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Players */}
              {formData.gameMode !== 'quickSolo' && (
                <div className="create-tournament-field">
                  <label className="create-tournament-label">{t('players', 'Players')}</label>
                  <select
                    value={formData.maxPlayers}
                    onChange={(e) => setFormData({...formData, maxPlayers: parseInt(e.target.value)})}
                    className="create-tournament-select"
                    disabled={loading}
                  >
                    {['teamDeathmatch', 'domination', 'searchAndDestroy', 'searchAndRescue', 'hardpoint', 'killConfirmed', 'frontline'].includes(formData.gameMode) ? (
                      formData.multiplayerType === 'team' || formData.multiplayerType === 'custom' ? (
                        <>
                          <option value={2}>{t('upTo', 'Up to')} 2 {t('players', 'Players')} (1v1)</option>
                          <option value={4}>{t('upTo', 'Up to')} 4 {t('players', 'Players')} (2v2)</option>
                          <option value={6}>{t('upTo', 'Up to')} 6 {t('players', 'Players')} (3v3)</option>
                          <option value={8}>{t('upTo', 'Up to')} 8 {t('players', 'Players')} (4v4)</option>
                          <option value={10}>{t('upTo', 'Up to')} 10 {t('players', 'Players')} (5v5)</option>
                        </>
                      ) : (
                        Array.from({ length: 9 }, (_, i) => i + 2).map(num => (
                          <option key={num} value={num}>{t('upTo', 'Up to')} {num} {t('players', 'Players')}</option>
                        ))
                      )
                    ) : formData.gameMode === 'battleRoyale' ? (
                      formData.multiplayerType === 'solo' ? (
                        // Solo BR: 2 to 100 players (99 options)
                        Array.from({ length: 99 }, (_, i) => i + 2).map(num => (
                          <option key={num} value={num}>{num} {t('players', 'Players')}</option>
                        ))
                      ) : formData.multiplayerType === 'duo' ? (
                        // Duo BR: 4 to 100 players in steps of 2 (49 teams max: 4,6,8...100)
                        Array.from({ length: 49 }, (_, i) => (i + 2) * 2).map(num => (
                          <option key={num} value={num}>{t('upTo', 'Up to')} {num} {t('players', 'Players')} ({num/2} {t('teams', 'Teams')})</option>
                        ))
                      ) : (
                        // Squad BR: 8 to 100 players in steps of 4 (24 teams max: 8,12,16...100)
                        Array.from({ length: 24 }, (_, i) => (i + 2) * 4).map(num => (
                          <option key={num} value={num}>{t('upTo', 'Up to')} {num} {t('players', 'Players')} ({num/4} {t('teams', 'Teams')})</option>
                        ))
                      )
                    ) : (
                      Array.from({ length: 7 }, (_, i) => i + 2).map(num => (
                        <option key={num} value={num}>{t('upTo', 'Up to')} {num} {t('players', 'Players')}</option>
                      ))
                    )}
                  </select>
                </div>
              )}

              {formData.gameMode === 'quickSolo' && (
                <div className="create-tournament-field">
                  <label className="create-tournament-label">{t('players', 'Players')}</label>
                  <div className="create-tournament-input-display">
                    1v1 (2 {t('players', 'Players')}) - {t('locked', 'Locked')}
                  </div>
                </div>
              )}
              
              {/* Entry Fee / Total Prize Pool */}
              <div className="create-tournament-field">
                <label className="create-tournament-label">
                  {formData.tournamentType === 'giveaway' ? t('totalPrizePool', 'Total Prize Pool') : t('entryFee', 'Entry Fee')}
                </label>
                <input
                  type="number"
                  value={formData.entryFee}
                  onChange={(e) => setFormData({...formData, entryFee: parseInt(e.target.value) || 0})}
                  className="create-tournament-input"
                  min={formData.tournamentType === 'giveaway' ? (isAdminRole ? "0" : "200") : "10"}
                  step="10"
                  disabled={loading}
                  placeholder={formData.tournamentType === 'giveaway' ?
                    t('totalPrizeForWinners', 'Total prize for winner(s)') :
                    t('entryFeePerPlayer', 'Entry fee per player')}
                />
              </div>

              {/* Max Kills */}
              {['teamDeathmatch', 'domination', 'hardpoint', 'killConfirmed', 'frontline', 'freeForAll'].includes(formData.gameMode) && (
                <div className="create-tournament-field">
                  <label className="create-tournament-label">
                    {t('maxKills', 'Max Kills')}
                  </label>
                  <input
                    type="number"
                    value={formData.maxKills || 20}
                    onChange={(e) => setFormData({...formData, maxKills: parseInt(e.target.value) || 20})}
                    className="create-tournament-input"
                    min="10"
                    max="30"
                    step="5"
                    disabled={loading}
                  />
                </div>
              )}

              {/* Quick Solo - Kill Limit */}
              {formData.gameMode === 'quickSolo' && (
                <div className="create-tournament-field">
                  <label className="create-tournament-label">{t('killLimit', 'Kill Limit')}</label>
                  <input
                    type="number"
                    value={formData.killLimit || 10}
                    onChange={(e) => setFormData({...formData, killLimit: parseInt(e.target.value) || 10})}
                    className="create-tournament-input"
                    min="1"
                    max="25"
                    step="1"
                    disabled={loading}
                  />
                </div>
              )}

              {/* Quick Solo - Time Limit */}
              {formData.gameMode === 'quickSolo' && (
                <div className="create-tournament-field">
                  <label className="create-tournament-label">{t('timeLimit', 'Time Limit (seconds)')}</label>
                  <input
                    type="number"
                    value={formData.timeLimit || 300}
                    onChange={(e) => setFormData({...formData, timeLimit: parseInt(e.target.value) || 300})}
                    className="create-tournament-input"
                    min="60"
                    max="600"
                    step="30"
                    disabled={loading}
                  />
                </div>
              )}

              {/* Map Dropdown */}
              <div className="create-tournament-field">
                <label className="create-tournament-label">{t('map', 'Map')}</label>
                <select
                  value={formData.map}
                  onChange={(e) => setFormData({...formData, map: e.target.value})}
                  className="create-tournament-select"
                  disabled={loading || formData.gameMode === 'quickSolo'}
                >
                  {(codMobileMaps[formData.gameMode] || codMobileMaps.teamDeathmatch).map((mapName) => (
                    <option key={mapName} value={mapName}>
                      {mapName === 'Random' ? t('randomMap', '🎲 Random Map') : `🗺️ ${mapName}`}
                      {formData.gameMode === 'quickSolo' && mapName === 'Shipment' ? ` (${t('locked', 'Locked')})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Password */}
              <div className="create-tournament-field">
                <label className="create-tournament-label">{t('roomPassword', 'Room Password')} ({t('optional', 'Optional')})</label>
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="create-tournament-input"
                  placeholder={t('optional', 'Optional')}
                  disabled={loading}
                />
              </div>

            </div>

            {/* Prize Pool Display */}
            <div className={`create-tournament-prize-display ${formData.tournamentType === 'giveaway' ? 'giveaway' : 'regular'}`}>
              <div className="create-tournament-prize-header">
                <div className="create-tournament-prize-icon">
                  {formData.tournamentType === 'giveaway' ? (
                    <Gift className="w-6 h-6 text-white" />
                  ) : (
                    <Plus className="w-6 h-6 text-white" />
                  )}
                </div>
                <h4 className={`create-tournament-prize-title ${formData.tournamentType === 'giveaway' ? 'giveaway' : 'regular'}`}>
                  {formData.tournamentType === 'giveaway' ? 
                    t('giveawayIndicator', '🎁 Giveaway Tournament') : 
                    formData.gameMode === 'quickSolo' ? 
                    '⚡ ' + t('quickSoloTournament', 'Quick Solo Tournament') :
                    '🏆 ' + t('regularTournament', 'Regular Tournament')}
                </h4>
              </div>
              
              <div className="create-tournament-prize-details">
                <p className={`create-tournament-prize-item ${formData.tournamentType === 'giveaway' ? 'giveaway' : 'regular'}`}>
                  🏆 {t('prizePoolForWinners', 'Prize Pool for Winners')}: <span className="text-green-400 text-lg">{calculatePrizePool()} {t('points', 'Points')}</span>
                </p>
                
                {formData.tournamentType === 'giveaway' ? (
                  <>
                    <p className={`create-tournament-prize-sub ${formData.tournamentType === 'giveaway' ? 'giveaway' : 'regular'}`}>
                      🆓 {t('entryFeeForPlayers', 'Entry Fee for Players')}: <strong className="text-green-400">{t('free', 'FREE')}</strong>
                    </p>
                    <p className={`create-tournament-prize-sub ${formData.tournamentType === 'giveaway' ? 'giveaway' : 'regular'}`}>
                      💸 {t('yourTotalCost', 'Your Total Cost')}: <strong className="create-tournament-prize-cost">{calculateCreatorCost()} {t('points', 'Points')}</strong>
                    </p>
                  </>
                ) : (
                  <p className={`create-tournament-prize-sub ${formData.tournamentType === 'giveaway' ? 'giveaway' : 'regular'}`}>
                    💸 {t('yourEntryFee', 'Your Entry Fee')}: <strong className="text-yellow-400">{formData.entryFee} {t('points', 'Points')}</strong>
                  </p>
                )}

                {formData.gameMode === 'quickSolo' && (
                  <div className="mt-2 space-y-1">
                    <p className={`create-tournament-prize-sub ${formData.tournamentType === 'giveaway' ? 'giveaway' : 'regular'}`}>
                      🎯 {t('killLimit', 'Kill Limit')}: <strong className="text-blue-400">{formData.killLimit}</strong>
                    </p>
                    <p className={`create-tournament-prize-sub ${formData.tournamentType === 'giveaway' ? 'giveaway' : 'regular'}`}>
                      ⏱️ {t('timeLimit', 'Time Limit')}: <strong className="text-blue-400">{Math.floor(formData.timeLimit / 60)}:{(formData.timeLimit % 60).toString().padStart(2, '0')}</strong>
                    </p>
                    <p className={`create-tournament-prize-sub ${formData.tournamentType === 'giveaway' ? 'giveaway' : 'regular'}`}>
                      🗺️ {t('map', 'Map')}: <strong className="text-purple-400">Shipment ({t('locked', 'Locked')})</strong>
                    </p>
                  </div>
                )}

                {(() => {
                  const breakdown = getCommissionBreakdown();
                  return (
                    <div className="mt-3 space-y-1">
                      <p className={`create-tournament-prize-commission ${formData.tournamentType === 'giveaway' ? 'giveaway' : 'regular'}`}>
                        📊 {t('totalCommission', 'Total Commission')} ({breakdown.totalRate}%): {breakdown.totalCommissionAmount} {t('pts', 'pts')}
                        {breakdown.loading && <span className="text-yellow-400 text-xs ml-2">(loading...)</span>}
                      </p>
                    </div>
                  );
                })()}
              </div>
              
              <div className={`create-tournament-prize-info ${formData.tournamentType === 'giveaway' ? 'giveaway' : 'regular'}`}>
                <p>
                  <strong>ℹ️ {t('howItWorks', 'How it works')}:</strong> 
                  {formData.gameMode === 'quickSolo' 
                    ? t('howItWorksQuickSolo', ' Quick 1v1 match on Shipment. First to {kills} kills or highest score at {time} wins!')
                        .replace('{kills}', formData.killLimit)
                        .replace('{time}', `${Math.floor(formData.timeLimit / 60)}:${(formData.timeLimit % 60).toString().padStart(2, '0')}`)
                    : formData.tournamentType === 'giveaway' 
                      ? t('howItWorksGiveaway', ' You pay {cost} points ({prize} prize + {commission} commission). Players join for free. Winner(s) receive exactly {prizePool} points.')
                          .replace('{cost}', calculateCreatorCost())
                          .replace('{prize}', formData.entryFee)
                          .replace('{commission}', calculateCreatorCost() - formData.entryFee)
                          .replace('{prizePool}', calculatePrizePool())
                      : t('howItWorksRegular', ' Total entry fees: {totalEntry} pts. After {commissionRate}% commission, prize pool is {prizePool} pts.')
                          .replace('{totalEntry}', formData.entryFee * formData.maxPlayers)
                          .replace('{commissionRate}', getActualCommissionRate())
                          .replace('{prizePool}', calculatePrizePool())
                  }
                </p>
              </div>
            </div>

            {formData.gameMode === 'quickSolo' && formData.allowedWeapons.length > 0 && (
              <div className="create-tournament-warning custom-mode">
                <p>
                  {t('weaponRestrictionsWarning', '⚠️ Using restricted weapons will result in instant disqualification!')}
                </p>
                <p className="text-sm mt-1">
                  {t('allowedWeaponOnly', 'Only this weapon is allowed:')}{' '}
                  <strong>
                    {formData.allowedWeapons.map(weaponId => {
                      const weapon = weaponTypes.find(w => w.id === weaponId);
                      return weapon ? weapon.name : weaponId;
                    }).join(', ')}
                  </strong>
                </p>
              </div>
            )}

            {formData.gameMode === 'quickSolo' && (
              <div className="create-tournament-warning quick-solo">
                <p>
                  ⚡ {t('quickSoloInfo', 'Quick Solo: Fast-paced 1v1 matches on Shipment only. Perfect for quick point battles!')}
                  {formData.allowedWeapons.length === 0 && (
                    <span className="block text-sm mt-1">
                      {t('allWeaponsAllowed', 'All weapons allowed - no restrictions.')}
                    </span>
                  )}
                </p>
              </div>
            )}
            
            {user.points < calculateCreatorCost() && (
              <div className="create-tournament-warning insufficient-points">
                <p>
                  ❌ {t('insufficientPointsDetailed', 'Insufficient points! You need {needed} points but only have {current} points.')
                    .replace('{needed}', calculateCreatorCost())
                    .replace('{current}', user.points)}
                  {formData.tournamentType === 'giveaway' && (
                    <span className="create-tournament-warning-block">
                      💡 {t('giveawayExplanation', 'In giveaway tournaments, you pay {prize} points (prize) + {commission} points (commission) = {total} total')
                        .replace('{prize}', formData.entryFee)
                        .replace('{commission}', calculateCreatorCost() - formData.entryFee)
                        .replace('{total}', calculateCreatorCost())}
                    </span>
                  )}
                </p>
              </div>
            )}
            
            <div className="create-tournament-actions">
              <button
                type="submit"
                disabled={loading || user.points < calculateCreatorCost()}
                className={`create-tournament-btn create-tournament-btn-submit ${formData.tournamentType === 'giveaway' ? 'giveaway' : ''}`}
              >
                {formData.tournamentType === 'giveaway' && <Gift className="w-4 h-4" />}
                {loading ? t('creating', 'Creating...') : 
                  formData.tournamentType === 'giveaway' 
                    ? t('createGiveawayTournament', 'Create Giveaway Tournament')
                    : formData.gameMode === 'quickSolo'
                    ? t('createQuickSolo', 'Create Quick Solo')
                    : t('createRegularTournament', 'Create Tournament')
                }
              </button>
              
              <button
                type="button"
                onClick={toggleMinimized}
                className="create-tournament-btn create-tournament-btn-minimize"
              >
                <Minus className="w-4 h-4" />
                {t('minimize', 'Minimize')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CreateTournament;